import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './HostEvent.css';

/* The wall. What the room reads while it stands up and walks to a corner.

   ★ It reuses `.hv-present` — the survey projector's shell — rather than growing a second
   full-screen mode. Same background, same exit button in the same corner, same growth with the
   viewport, so a host who has projected the answers already knows how to leave this.

   ★ The interval lives in a REF. Restarting a timer whenever the data changes is how a projector
   left open all evening ends up refreshing every second by the end of it — the bug the survey
   projector documents and this would have repeated.

   ★ It shows the last PUBLISHED draw by default, never the last created. A host preparing the
   next round while this one is still on the wall must not blank the wall. `?b=<id>` pins one. */

// The same six seconds the survey projector polls at: slow enough not to hammer the API from a
// laptop left open all evening, fast enough that a name moved between groups appears while the
// person is still walking.
const BOARD_POLL_MS = 6000;

const BreakoutBoard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [params] = useSearchParams();
  // Read once at mount: `usePersistUrlParams` strips query params a tick after they appear, so a
  // pinned draw would not survive its own navigation. Same reason the survey projector does it.
  const [pinned] = useState(() => params.get('b') || '');

  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    const suffix = pinned ? `?b=${encodeURIComponent(pinned)}` : '';
    apiRequest('GET', `/company/event/${eventId}/breakout-board${suffix}`)
      .then((res) => { setData(res); setFailed(false); })
      // A poll that fails must not blank a wall people are reading. Keep what is on screen and
      // say so quietly; the next tick usually fixes it.
      .catch(() => setFailed(true));
  }, [eventId, pinned]);

  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    loadRef.current();
    const id = setInterval(() => loadRef.current(), BOARD_POLL_MS);
    return () => clearInterval(id);
  }, [eventId, pinned]);

  const breakout = data && data.breakout;
  const groups = (breakout && breakout.groups) || [];

  return (
    <div className="hv-present">
      <button
        type="button"
        className="hv-present__exit"
        onClick={() => navigate(`/host-event/${eventId}/breakouts`)}
      >
        <Icon name="cross-circle" /> {t('leavePresentMode')}
      </button>

      <h1 className="hv-present__title">
        {(breakout && breakout.name) || t('breakouts')}
      </h1>
      <p className="hv-present__sub">
        {[(data && data.event_name) || '', breakout && breakout.planning_title]
          .filter(Boolean).join(' · ')}
        {failed && ` · ${t('reconnecting')}`}
      </p>

      <div className="hv-present__body">
        {data === null && <span className="auth__spinner" />}
        {data !== null && !breakout && <p>{t('noBreakoutYet')}</p>}
        <div className="hv-board">
          {groups.map((g) => (
            <div key={g.id} className="hv-board__group">
              <p className="hv-board__name mb-0">
                {(g.name || '').trim() || t('breakoutGroupN', { n: g.sort_order })}
              </p>
              {g.location && <p className="hv-board__where mb-0">{g.location}</p>}
              {g.members.map((m) => (
                <p key={m.id} className="hv-board__member mb-0">{m.name}</p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BreakoutBoard;
